import React from "react";
import { AgGridReact } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { TypedAGGridCellRenderParams } from "~/pages/Compliance/ComplianceTable/types";
import { FieldKey } from "./constants";
import * as styles from "./styles_vanilla.css";
import { ValueCell, type DocumentData } from "./ValueCell";
export { FieldKey } from "./constants";

export type ComparisonRowData = {
  field: string;
  fieldKey: FieldKey;
  current: string;
  proposed: string;
  isDocument?: boolean;
  currentDocs?: DocumentData[];
  proposedDocs?: DocumentData[];
  amlQuestionId?: number;
  amlQuestionCount?: number;
};

type GetColumnDefsOptions = {
  onDocumentClick?: (doc: number, profileId: number) => void;
  onDeleteRow?: (row: ComparisonRowData) => void;
  sourceProfileId: number;
  targetProfileId: number;
  readonly?: boolean;
};

export const getColumnDefs = (
  options?: GetColumnDefsOptions,
): AgGridReact.AgGridReactProps["columnDefs"] => [
  {
    field: "field",
    headerName: "",
    width: 240,
    sortable: false,
    cellClass: styles.fieldCell,
  },
  {
    field: "current",
    headerName: "Current",
    width: 328,
    sortable: false,
    headerClass: styles.columnHeader,
    cellClass: styles.valueCellWithBorder,
    cellRenderer: (params: TypedAGGridCellRenderParams<ComparisonRowData>) => (
      <ValueCell
        value={params.value}
        docs={params.data.currentDocs}
        isDocument={params.data.isDocument}
        onDocumentClick={(docId: number) =>
          options?.onDocumentClick?.(docId, options?.sourceProfileId ?? 0)
        }
      />
    ),
    wrapText: true,
    autoHeight: true,
  },
  {
    field: "proposed",
    headerName: "Proposed",
    width: 328,
    sortable: false,
    headerClass: styles.columnHeader,
    cellClass: styles.valueCell,
    cellRenderer: (params: TypedAGGridCellRenderParams<ComparisonRowData>) => (
      <ValueCell
        value={params.value}
        docs={params.data.proposedDocs}
        isDocument={params.data.isDocument}
        onDocumentClick={(docId: number) =>
          options?.onDocumentClick?.(docId, options?.targetProfileId ?? 0)
        }
      />
    ),
    wrapText: true,
    autoHeight: true,
  },
  {
    field: "actions",
    headerName: "",
    width: 40,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    cellClass: styles.actionsCell,
    rowSpan: (params) => {
      const data = params.data as ComparisonRowData | undefined;
      return data?.amlQuestionCount ?? 1;
    },
    cellRenderer: (params: TypedAGGridCellRenderParams<ComparisonRowData>) => {
      if (!params.data) return null;

      const isDisabled = options?.readonly;

      return (
        <Icon
          type="trash"
          variant="outline"
          size="medium"
          className={isDisabled ? styles.deleteIconDisabled : styles.deleteIcon}
          data-testid="delete-row"
          onClick={() => !isDisabled && options?.onDeleteRow?.(params.data)}
        />
      );
    },
  },
];

export const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  suppressCellFocus: true,
  domLayout: "autoHeight",
  rowHeight: 46,
  headerHeight: 48,
  suppressRowTransform: true,
};
